/**
 * LatitudeLongitude - Represents latitude and longitude coordinates in DMS format.
 * Used to store start and end latitude/longitude values with degrees, minutes, and seconds.
 */

/**
 * DMS (Degrees, Minutes, Seconds) representation for latitude/longitude
 */
export interface DMS {
  /** Degrees component */
  degrees: number;
  /** Minutes component */
  minutes: number;
  /** Seconds component */
  seconds: number;
}

export class LatitudeLongitude {
  /** Starting latitude in DMS format */
  LatStart: DMS;

  /** Ending latitude in DMS format */
  LatEnd: DMS;

  /** Starting longitude in DMS format */
  LongStart: DMS;

  /** Ending longitude in DMS format */
  LongEnd: DMS;

  /**
   * Creates a new LatitudeLongitude instance
   * @param data - Object containing latitude/longitude properties
   */
  constructor(data: {
    LatStart: DMS;
    LatEnd: DMS;
    LongStart: DMS;
    LongEnd: DMS;
  }) {
    this.LatStart = data.LatStart;
    this.LatEnd = data.LatEnd;
    this.LongStart = data.LongStart;
    this.LongEnd = data.LongEnd;
  }

  /**
   * Creates a LatitudeLongitude from a plain object (useful for deserialization)
   * @param obj - Plain object with latitude/longitude data
   * @returns New LatitudeLongitude instance
   */
  static fromObject(obj: Partial<LatitudeLongitude>): LatitudeLongitude {
    const createDMS = (dms: any): DMS => {
      if (dms && typeof dms === "object" && "degrees" in dms && "minutes" in dms && "seconds" in dms) {
        return {
          degrees: typeof dms.degrees === "number" ? dms.degrees : 0,
          minutes: typeof dms.minutes === "number" ? dms.minutes : 0,
          seconds: typeof dms.seconds === "number" ? dms.seconds : 0,
        };
      }
      return { degrees: 0, minutes: 0, seconds: 0 };
    };

    return new LatitudeLongitude({
      LatStart: createDMS(obj.LatStart),
      LatEnd: createDMS(obj.LatEnd),
      LongStart: createDMS(obj.LongStart),
      LongEnd: createDMS(obj.LongEnd),
    });
  }

  /**
   * Converts the LatitudeLongitude to a plain object (useful for serialization)
   * @returns Plain object representation
   */
  toObject(): Record<string, unknown> {
    return {
      LatStart: this.LatStart,
      LatEnd: this.LatEnd,
      LongStart: this.LongStart,
      LongEnd: this.LongEnd,
    };
  }
}


